import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import PublicTaskView from "@/pages/PublicTaskView";
import HomePage from "@/pages/HomePage";
import PricingPage from "@/pages/PricingPage";
import AboutPage from "@/pages/AboutPage";
import HelpPage from "@/pages/HelpPage";
import ChangelogPage from "@/pages/ChangelogPage";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import ComparePage from "@/pages/ComparePage";
import UseCasePage from "@/pages/UseCasePage";
import BlogAdminPage from "@/pages/BlogAdminPage";
import BillingPage from "@/pages/BillingPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { useEffect } from "react";
import "@/App.css";

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        data-testid="auth-loading"
      >
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      const params = new URLSearchParams(location.search);
      const next = params.get("next") || "/app";
      nav(next, { replace: true });
    }
  }, [user, nav, location.search]);

  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return <LoginPage />;
}

export default function App() {
  return (
    <AuthProvider>
      <Toaster
        position="top-right"
        offset={16}
        mobileOffset={{ top: 80, right: 12, left: 12 }}
        toastOptions={{ style: { pointerEvents: "auto" } }}
        className="!pointer-events-none"
      />
      <Routes>
        {/* Public marketing + SEO pages */}
        <Route path="/" element={<HomePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/compare/:competitor" element={<ComparePage />} />
        <Route path="/use-cases/:slug" element={<UseCasePage />} />

        {/* Public read-only share */}
        <Route path="/share/:token" element={<PublicTaskView />} />

        {/* Auth + app */}
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/app/*"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/blog"
          element={
            <RequireAuth>
              <BlogAdminPage />
            </RequireAuth>
          }
        />
        <Route
          path="/billing"
          element={
            <RequireAuth>
              <BillingPage />
            </RequireAuth>
          }
        />

        {/* 404 → home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
